import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Alert,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalanceWallet as CajaIcon,
  LockOpen as AbrirIcon,
  Lock as CerrarIcon,
  Summarize as SummaryIcon,
} from '@mui/icons-material';
import { usePosSession } from '../hooks/usePosSession';
import { usePosStore } from '../store/posStore';
import { formatCurrency } from '../utils/posCalculations';
import { PosCajaService } from '../services/posCajaService';
import { CashSummaryDialog } from './CashSummaryDialog';
import { http } from '../../../api/http';
import type { AbrirCajaRequest, ResumenCajaResponse } from '../types/pos.types';
import { initialAbrirCaja } from '../types/pos.types';

interface PosConfig {
  cajas: { idCajaBanco: number; codigo: string; nombre: string; tipoCajaBanco: string }[];
  sucursales: { idSucursal: number; nombre: string }[];
  puntosDeVenta: { idPuntoVenta: number; numeroPuesto: number; identificacion: string; idSucursal: number }[];
}

export const CajaDialog: React.FC = () => {
  const { sesionActiva, hasSesion, abrirSesion, cerrarSesion, sesionLoading } = usePosSession();
  const { cajaDialogOpen, setCajaDialogOpen } = usePosStore();
  const [formData, setFormData] = useState<AbrirCajaRequest>(initialAbrirCaja);
  const [montoCierre, setMontoCierre] = useState<number>(0);
  const [observaciones, setObservaciones] = useState('');
  const [resumen, setResumen] = useState<ResumenCajaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [config, setConfig] = useState<PosConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);

  useEffect(() => {
    if (cajaDialogOpen && !hasSesion) {
      setConfigLoading(true);
      http.get('/pos/config')
        .then((res) => setConfig(res.data))
        .catch(() => setConfig(null))
        .finally(() => setConfigLoading(false));
    }
    if (cajaDialogOpen && hasSesion && sesionActiva) {
      PosCajaService.getResumen()
        .then(setResumen)
        .catch(() => setResumen(null));
    }
  }, [cajaDialogOpen, hasSesion, sesionActiva]);

  // Auto-set sucursal when punto de venta changes
  useEffect(() => {
    if (formData.idPuntoDeVenta && config) {
      const pdv = config.puntosDeVenta.find((p) => p.idPuntoVenta === formData.idPuntoDeVenta);
      if (pdv) {
        setFormData((p) => ({ ...p, idSucursal: pdv.idSucursal }));
      }
    }
  }, [formData.idPuntoDeVenta, config]);

  const handleAbrir = async () => {
    if (!formData.idCajaBanco) {
      setError('Seleccione una caja');
      return;
    }
    if (!formData.idPuntoDeVenta) {
      setError('Seleccione un punto de venta');
      return;
    }
    if (!formData.idSucursal) {
      setError('Seleccione una sucursal');
      return;
    }
    if (formData.montoApertura < 0) {
      setError('El monto de apertura no puede ser negativo');
      return;
    }
    setError(null);
    try {
      await abrirSesion(formData);
      setFormData(initialAbrirCaja);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al abrir caja');
    }
  };

  const handleCerrar = async () => {
    if (!sesionActiva) return;
    setError(null);
    try {
      await cerrarSesion({
        idSesionCaja: sesionActiva.idSesionCaja,
        montoCierre,
        observaciones: observaciones || undefined,
      });
      setMontoCierre(0);
      setObservaciones('');
      setCajaDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cerrar caja');
    }
  };

  const puntosDeVentaFiltrados = config?.puntosDeVenta ?? [];

  return (
    <Dialog open={cajaDialogOpen} onClose={() => setCajaDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CajaIcon color="primary" />
          {hasSesion ? 'Cerrar Caja' : 'Abrir Caja'}
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!hasSesion ? (
          // Open cash dialog
          configLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                select
                label="Caja"
                size="small"
                value={formData.idCajaBanco || ''}
                onChange={(e) => setFormData((p) => ({ ...p, idCajaBanco: Number(e.target.value) }))}
              >
                <MenuItem value="">-- Seleccione --</MenuItem>
                {config?.cajas.map((c) => (
                  <MenuItem key={c.idCajaBanco} value={c.idCajaBanco}>
                    [{c.tipoCajaBanco}] {c.codigo} - {c.nombre}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Punto de Venta"
                size="small"
                value={formData.idPuntoDeVenta || ''}
                onChange={(e) => setFormData((p) => ({ ...p, idPuntoDeVenta: Number(e.target.value) }))}
              >
                <MenuItem value="">-- Seleccione --</MenuItem>
                {puntosDeVentaFiltrados.map((p) => (
                  <MenuItem key={p.idPuntoVenta} value={p.idPuntoVenta}>
                    Puesto {p.numeroPuesto} - {p.identificacion}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Sucursal"
                size="small"
                value={formData.idSucursal || ''}
                onChange={(e) => setFormData((p) => ({ ...p, idSucursal: Number(e.target.value) }))}
              >
                <MenuItem value="">-- Seleccione --</MenuItem>
                {config?.sucursales.map((s) => (
                  <MenuItem key={s.idSucursal} value={s.idSucursal}>
                    {s.nombre}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Monto de Apertura"
                type="number"
                size="small"
                value={formData.montoApertura || ''}
                onChange={(e) => setFormData((p) => ({ ...p, montoApertura: Number(e.target.value) }))}
                slotProps={{ htmlInput: { min: 0, step: 0.1 } }}
              />
            </Box>
          )
        ) : (
          // Close cash dialog
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {resumen && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Monto Apertura</Typography>
                  <Typography fontWeight="bold">{formatCurrency(resumen.montoApertura)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Ventas ({resumen.cantidadVentas})</Typography>
                  <Typography fontWeight="bold" color="success.main">+{formatCurrency(resumen.totalVentas)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Ingresos ({resumen.cantidadIngresos})</Typography>
                  <Typography fontWeight="bold" color="success.main">+{formatCurrency(resumen.totalIngresos)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Egresos ({resumen.cantidadEgresos})</Typography>
                  <Typography fontWeight="bold" color="error.main">-{formatCurrency(resumen.totalEgresos)}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight="bold">Monto Esperado</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {formatCurrency(resumen.montoEsperado)}
                  </Typography>
                </Box>
              </>
            )}

            <Divider />

            <TextField
              label="Monto de Cierre (conteo real)"
              type="number"
              size="small"
              value={montoCierre || ''}
              onChange={(e) => setMontoCierre(Number(e.target.value))}
              slotProps={{ htmlInput: { min: 0, step: 0.1 } }}
            />

            {montoCierre > 0 && resumen && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: Math.abs(montoCierre - resumen.montoEsperado) < 1 ? 'success.light' : 'warning.light',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Diferencia: {formatCurrency(montoCierre - resumen.montoEsperado)}
                </Typography>
              </Box>
            )}

            <TextField
              label="Observaciones"
              size="small"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              multiline
              rows={2}
            />

            <Button
              variant="outlined"
              size="small"
              startIcon={<SummaryIcon />}
              onClick={() => setSummaryOpen(true)}
            >
              Ver Resumen Detallado
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => setCajaDialogOpen(false)} disabled={sesionLoading}>
          Cancelar
        </Button>
        {!hasSesion ? (
          <Button
            variant="contained"
            onClick={handleAbrir}
            disabled={sesionLoading || configLoading}
            startIcon={<AbrirIcon />}
          >
            {sesionLoading ? 'Abriendo...' : 'Abrir Caja'}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            onClick={handleCerrar}
            disabled={sesionLoading}
            startIcon={<CerrarIcon />}
          >
            {sesionLoading ? 'Cerrando...' : 'Cerrar Caja'}
          </Button>
        )}
      </DialogActions>

      <CashSummaryDialog open={summaryOpen} onClose={() => setSummaryOpen(false)} />
    </Dialog>
  );
};

export default CajaDialog;
